import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { STYLE_PRESETS, AI_MODELS } from '../lib/constants';

function NarrationPanel({ pointId, lang, onClose }) {
  const [narrations, setNarrations] = useState([]);
  const [activeNarration, setActiveNarration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [generateOptions, setGenerateOptions] = useState({
    style_preset: 'informative',
    max_chars: 1000,
    model: 'gpt-4o-mini',
  });
  const [regenerateOptions, setRegenerateOptions] = useState({
    changes: '',
    max_chars: 1000,
    model: 'gpt-4o-mini',
  });
  
  // TTS state
  const [voices, setVoices] = useState([]);
  const [audioAsset, setAudioAsset] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  const [selectedModel, setSelectedModel] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const isSeekingRef = useRef(false);

  useEffect(() => {
    loadVoices();
  }, []);

  useEffect(() => {
    if (pointId) {
      loadNarrations();
    }
  }, [pointId]);

  useEffect(() => {
    // Load audio asset when active narration changes
    if (activeNarration) {
      // Clean up previous audio
      if (audioElement) {
        audioElement.pause();
        if (audioElement.src && audioElement.src.startsWith('blob:')) {
          URL.revokeObjectURL(audioElement.src);
        }
      }
      setAudioElement(null);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      
      // Load new audio asset
      loadAudioAsset();
    }
  }, [activeNarration?.id]); // Use activeNarration.id to ensure effect runs when version changes

  // Update model when voice changes
  useEffect(() => {
    const voice = voices.find(v => v.id === selectedVoice);
    if (voice && voice.models && voice.models.length > 0) {
      // Set to default model if available, otherwise first model
      const defaultModel = voice.models.find(m => m.is_default);
      setSelectedModel(defaultModel ? defaultModel.id : voice.models[0].id);
    } else {
      setSelectedModel(null);
    }
  }, [selectedVoice, voices]);

  const loadVoices = async () => {
    try {
      console.log('Loading voices...');
      const response = await api.getVoices();
      console.log('Raw response:', JSON.stringify(response, null, 2));
      console.log('Response keys:', Object.keys(response));
      // Backend returns Voices (capital V) in the catalog
      const voicesList = response.voices || response.Voices || [];
      console.log('Voice list extracted:', JSON.stringify(voicesList, null, 2));
      console.log('Voice list length:', voicesList.length);
      if (voicesList.length > 0) {
        console.log('First voice:', JSON.stringify(voicesList[0], null, 2));
      }
      setVoices(voicesList);
      console.log('Voices state set');
    } catch (err) {
      console.error('Failed to load voices - full error:', err);
      console.error('Error message:', err.message);
      // Not a critical error, continue with empty list
    }
  };

  const loadNarrations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getNarrations(pointId);
      const narrs = response.narrations || [];
      setNarrations(narrs);
      
      // Find active narration
      const active = narrs.find(n => n.status === 'active');
      setActiveNarration(active || narrs[0] || null);
      if (active) {
        setEditText(active.text);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAudioAsset = async () => {
    if (!activeNarration) {
      setAudioAsset(null);
      return;
    }
    
    try {
      // Try to fetch audio asset for this specific narration version
      const asset = await api.getAudioByNarrationId(activeNarration.id);
      if (asset && asset.status === 'ready') {
        setAudioAsset(asset);
        if (asset.voice) {
          setSelectedVoice(asset.voice);
        }
        if (asset.model) {
          setSelectedModel(asset.model);
        }
      } else {
        setAudioAsset(null);
      }
    } catch (err) {
      // Audio not found is not an error, just means it hasn't been generated yet
      setAudioAsset(null);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);
      const narration = await api.generateNarration(pointId, {
        lang,
        ...generateOptions,
      });
      setActiveNarration(narration);
      setEditText(narration.text);
      await loadNarrations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!activeNarration) return;
    
    try {
      setLoading(true);
      setError(null);
      const narration = await api.regenerateNarration(
        pointId,
        activeNarration.version,
        regenerateOptions.changes,
        regenerateOptions.max_chars,
        regenerateOptions.model
      );
      setActiveNarration(narration);
      setEditText(narration.text);
      setShowRegenerate(false);
      setRegenerateOptions({ changes: '', max_chars: 500 });
      await loadNarrations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!activeNarration) return;
    
    try {
      setLoading(true);
      setError(null);
      await api.updateNarration(activeNarration.id, editText);
      await loadNarrations();
      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateVersion = async (narrationId) => {
    try {
      setLoading(true);
      setError(null);
      await api.activateNarration(narrationId);
      await loadNarrations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVersion = (narration) => {
    setActiveNarration(narration);
    setEditText(narration.text);
    setIsEditing(false);
    setShowHistory(false);
  };

  const handleGenerateAudio = async () => {
    if (!activeNarration) return;
    
    try {
      setGeneratingAudio(true);
      setError(null);
      
      // Clean up previous audio if exists
      if (audioElement) {
        audioElement.pause();
        if (audioElement.src && audioElement.src.startsWith('blob:')) {
          URL.revokeObjectURL(audioElement.src);
        }
      }
      setAudioElement(null);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      
      const asset = await api.generateAudio(activeNarration.id, selectedVoice, selectedModel);
      
      // Update selected voice and model from the generated asset
      if (asset && asset.voice) {
        setSelectedVoice(asset.voice);
      }
      if (asset && asset.model) {
        setSelectedModel(asset.model);
      }
      
      // Set the asset regardless of status - the UI will show generating/ready state
      setAudioAsset(asset);
      
      // If asset is ready immediately, great!
      // If it's still generating, poll until ready
      if (asset && asset.status === 'generating') {
        const pollInterval = setInterval(async () => {
          try {
            const updatedAsset = await api.getAudioByNarrationId(activeNarration.id);
            if (updatedAsset && updatedAsset.status === 'ready') {
              setAudioAsset(updatedAsset);
              clearInterval(pollInterval);
            } else if (updatedAsset && updatedAsset.status === 'failed') {
              setError('Audio generation failed');
              clearInterval(pollInterval);
            }
          } catch (err) {
            // Stop polling on error
            clearInterval(pollInterval);
          }
        }, 1000); // Poll every second
        
        // Stop polling after 30 seconds max
        setTimeout(() => clearInterval(pollInterval), 30000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const handlePlayAudio = async () => {
    if (!audioAsset || !audioAsset.id) return;
    
    try {
      // If we already have an audio element with a blob URL, just play it
      if (audioElement && audioElement.src && audioElement.src.startsWith('blob:')) {
        await audioElement.play();
        setIsPlaying(true);
        return;
      }
      
      // Fetch audio blob with authentication
      setError(null);
      const blob = await api.fetchAudioBlob(audioAsset.id);
      const blobUrl = URL.createObjectURL(blob);
      
      // Create new audio element with blob URL
      const audio = new Audio(blobUrl);
      
      // Add event listeners
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      
      audio.addEventListener('timeupdate', () => {
        // Don't update time display while user is seeking
        if (!isSeekingRef.current) {
          setCurrentTime(audio.currentTime);
        }
      });
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
      
      audio.addEventListener('pause', () => {
        setIsPlaying(false);
      });
      
      audio.addEventListener('play', () => {
        setIsPlaying(true);
      });
      
      setAudioElement(audio);
      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      console.error('Failed to play audio:', err);
      setError('Failed to play audio: ' + err.message);
    }
  };

  const handlePauseAudio = () => {
    if (audioElement) {
      audioElement.pause();
      setIsPlaying(false);
    }
  };

  const handleSeekStart = () => {
    isSeekingRef.current = true;
  };

  const handleSeek = (e) => {
    if (audioElement) {
      const newTime = parseFloat(e.target.value);
      setCurrentTime(newTime);
    }
  };

  const handleSeekEnd = (e) => {
    if (audioElement) {
      const newTime = parseFloat(e.target.value);
      audioElement.currentTime = newTime;
      setCurrentTime(newTime);
    }
    isSeekingRef.current = false;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const charCount = editText?.length || 0;
  const maxChars = generateOptions.max_chars;

  if (!pointId) {
    return (
      <div className="narration-panel">
        <p className="text-gray-500">Select a POI to manage narration</p>
      </div>
    );
  }

  return (
    <div className="narration-panel" style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '2px solid #e5e7eb' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
          Narration ({lang.toUpperCase()})
        </h3>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>
            ×
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: '0.75rem', marginBottom: '1rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33' }}>
          {error}
        </div>
      )}

      {!activeNarration && narrations.length === 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ marginBottom: '1rem', color: '#666' }}>No narration yet. Generate one to get started.</p>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
              AI Model
            </label>
            <select
              value={generateOptions.model}
              onChange={(e) => setGenerateOptions({ ...generateOptions, model: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              {AI_MODELS.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label} - {model.description}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Style Preset
            </label>
            <select
              value={generateOptions.style_preset}
              onChange={(e) => setGenerateOptions({ ...generateOptions, style_preset: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              {STYLE_PRESETS.map(preset => (
                <option key={preset.value} value={preset.value}>
                  {preset.label} - {preset.description}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Max Characters: {generateOptions.max_chars}
            </label>
            <input
              type="range"
              min="300"
              max="5000"
              step="100"
              value={generateOptions.max_chars}
              onChange={(e) => setGenerateOptions({ ...generateOptions, max_chars: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: loading ? '#ccc' : '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Generating...' : '✨ Generate Narration'}
          </button>
        </div>
      )}

      {activeNarration && (
        <div>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem', color: '#666' }}>
            <span>Version {activeNarration.version}</span>
            <span>•</span>
            <span className={`status-badge status-${activeNarration.status}`}>
              {activeNarration.status}
            </span>
            {narrations.length > 1 && (
              <>
                <span>•</span>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', padding: 0 }}
                >
                  {narrations.length} versions
                </button>
              </>
            )}
          </div>

          {showHistory && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <strong style={{ fontSize: '0.875rem' }}>Version History</strong>
                <button
                  onClick={() => setShowHistory(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem' }}
                >
                  ×
                </button>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {narrations.map(narr => (
                  <div
                    key={narr.id}
                    style={{
                      padding: '0.5rem',
                      marginBottom: '0.5rem',
                      backgroundColor: narr.id === activeNarration.id ? '#eff6ff' : '#f9fafb',
                      border: '1px solid',
                      borderColor: narr.id === activeNarration.id ? '#3b82f6' : '#e5e7eb',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleSelectVersion(narr)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>v{narr.version}</span>
                      <span className={`status-badge status-${narr.status}`} style={{ fontSize: '0.75rem' }}>
                        {narr.status}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#666', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {narr.text}
                    </p>
                    {narr.status !== 'active' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActivateVersion(narr.id);
                        }}
                        style={{
                          marginTop: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          backgroundColor: '#4f46e5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        Set Active
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {isEditing ? (
            <div style={{ marginBottom: '1rem' }}>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '150px',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: charCount > maxChars ? '#c33' : '#666' }}>
                  {charCount} / {maxChars} characters
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditText(activeNarration.text);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={loading}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: loading ? '#ccc' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  minHeight: '100px',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {activeNarration.text}
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
                {activeNarration.text.length} characters
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>
            )}
            <button
              onClick={() => setShowRegenerate(!showRegenerate)}
              style={{
                flex: 1,
                padding: '0.5rem',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Regenerate
            </button>
          </div>

          {showRegenerate && (
            <div style={{ padding: '0.75rem', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '0.875rem' }}>Regenerate Narration</strong>
                <button
                  onClick={() => setShowRegenerate(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem' }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  What would you like to change?
                </label>
                <textarea
                  value={regenerateOptions.changes}
                  onChange={(e) => setRegenerateOptions({ ...regenerateOptions, changes: e.target.value })}
                  placeholder="e.g., Make it more storytelling-focused, Add more historical details..."
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                  }}
                />
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  AI Model
                </label>
                <select
                  value={regenerateOptions.model}
                  onChange={(e) => setRegenerateOptions({ ...regenerateOptions, model: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  {AI_MODELS.map(model => (
                    <option key={model.value} value={model.value}>
                      {model.label} - {model.description}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  Max Characters: {regenerateOptions.max_chars}
                </label>
                <input
                  type="range"
                  min="300"
                  max="5000"
                  step="100"
                  value={regenerateOptions.max_chars}
                  onChange={(e) => setRegenerateOptions({ ...regenerateOptions, max_chars: parseInt(e.target.value) })}
                  style={{ width: '100%' }}
                />
              </div>

              <button
                onClick={handleRegenerate}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  backgroundColor: loading ? '#ccc' : '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
          )}

          <div style={{ padding: '0.75rem', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ fontSize: '0.875rem' }}>Audio</strong>
            </div>

            {audioAsset && audioAsset.status === 'ready' && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <button
                      onClick={isPlaying ? handlePauseAudio : handlePlayAudio}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      {isPlaying ? 'Pause' : 'Play'}
                    </button>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
                    <span style={{ minWidth: '35px' }}>{formatTime(currentTime)}</span>
                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onMouseDown={handleSeekStart}
                      onTouchStart={handleSeekStart}
                      onChange={handleSeek}
                      onMouseUp={handleSeekEnd}
                      onTouchEnd={handleSeekEnd}
                      style={{
                        flex: 1,
                        height: '4px',
                        borderRadius: '2px',
                        outline: 'none',
                        background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${(currentTime / duration) * 100}%, #ddd ${(currentTime / duration) * 100}%, #ddd 100%)`,
                        WebkitAppearance: 'none',
                        appearance: 'none',
                      }}
                    />
                    <span style={{ minWidth: '35px', textAlign: 'right' }}>{formatTime(duration)}</span>
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Voice
              </label>
              {voices.length === 0 ? (
                <div style={{ padding: '0.5rem', color: '#666', fontSize: '0.875rem' }}>
                  Loading voices...
                </div>
              ) : (
                <div>
                  {[
                    { key: 'openai', label: 'OpenAI' },
                    { key: 'elevenlabs', label: 'ElevenLabs' }
                  ].map(({ key, label }) => {
                    const providerVoices = voices.filter(v => v.provider === key);
                    if (providerVoices.length === 0) return null;
                    
                    return (
                      <div key={key} style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#666', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                          {label}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                          {providerVoices.map(voice => (
                            <div
                              key={voice.id}
                              onClick={(e) => {
                                console.log('Voice clicked:', voice.id);
                                e.stopPropagation();
                                setSelectedVoice(voice.id);
                              }}
                              style={{
                                padding: '0.5rem',
                                border: '2px solid',
                                borderColor: selectedVoice === voice.id ? '#4f46e5' : '#ddd',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                backgroundColor: selectedVoice === voice.id ? '#eff6ff' : 'white',
                              }}
                            >
                              <div style={{ fontWeight: '500', fontSize: '0.875rem', pointerEvents: 'none' }}>{voice.name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.125rem', pointerEvents: 'none' }}>
                                {voice.description}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Model selector - only show if selected voice has multiple models */}
            {(() => {
              const voice = voices.find(v => v.id === selectedVoice);
              if (!voice || !voice.models || voice.models.length <= 1) return null;
              
              return (
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                    Model
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {voice.models.map(model => {
                      console.log('Rendering model:', model.id, 'selected:', selectedModel);
                      return (
                        <div
                          key={model.id}
                          onClick={(e) => {
                            console.log('Model clicked:', model.id);
                            e.stopPropagation();
                            setSelectedModel(model.id);
                          }}
                          style={{
                            padding: '0.5rem',
                            border: '2px solid',
                            borderColor: selectedModel === model.id ? '#4f46e5' : '#ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: selectedModel === model.id ? '#eff6ff' : 'white',
                          }}
                        >
                          <div style={{ fontWeight: '500', fontSize: '0.875rem', pointerEvents: 'none' }}>
                            {model.name}
                            {model.is_default && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#666' }}>(default)</span>}
                          </div>
                          {model.description && (
                            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.125rem', pointerEvents: 'none' }}>
                              {model.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <button
              onClick={handleGenerateAudio}
              disabled={generatingAudio}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: generatingAudio ? '#ccc' : '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: generatingAudio ? 'not-allowed' : 'pointer',
                fontWeight: '500',
              }}
            >
              {generatingAudio ? 'Generating Audio...' : (audioAsset ? 'Regenerate Audio' : 'Generate Audio')}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .status-badge {
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .status-active {
          background-color: #dcfce7;
          color: #166534;
        }
        .status-generated {
          background-color: #dbeafe;
          color: #1e40af;
        }
        .status-edited {
          background-color: #fef3c7;
          color: #92400e;
        }
        .status-archived {
          background-color: #f3f4f6;
          color: #4b5563;
        }
        .status-draft {
          background-color: #f3f4f6;
          color: #6b7280;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #4f46e5;
          cursor: pointer;
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #4f46e5;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
}

export default NarrationPanel;
